import React from 'react';
import { Button, message, Space, Typography } from 'antd';
import { ArrowLeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { CustomButton } from '../../../components';
import { bulkCreate } from '../../../apis/shipment';
import { showCustomSuccessMessage } from '../../../common/utils';
import styles from './BulkUploadEditorFooter.module.css';

const { Text } = Typography;

// Type for data rows in the bulk upload
type DataRow = Record<string, unknown>;

// Type for error cell locations
interface ErrorCell {
  rowIndex: number;
  fieldKey: string;
}

// Type for API errors
// TODO(tim): move these API types to an appropriate API file
//  (probably requires BulkUploadEditor to be typed)
interface ApiError {
  status?: number;
  data?: {
    data?: {
      message?: string;
    };
  };
}

interface ApiResponse {
  header: HeaderItem[];
  data: DataRow[];
}

interface HeaderItem {
  key: string;
  label?: string;
}

interface BulkUploadFooterProps {
  currentErrorCellIndex: number;
  errorCells: ErrorCell[];
  token: string;
  goToError: (direction: 'up' | 'down') => void;
  onBackToStep1: () => void;
  unauthorize: (error: ApiError) => void;
  shipmentCount: string; // Image URL for shipment count icon
  totalShipments: number;
  totalRecords: number;
  onCancel: () => void;
  data: DataRow[];
  isRowValid: (row: DataRow) => boolean;
  prepareCleanedPayload: (
    apiResponse: ApiResponse,
    data: DataRow[]
  ) => ApiResponse;
  apiResponse: ApiResponse;
  showSpin: () => void;
  hideSpin: () => void;
}

/**
 * Footer component for bulk upload editor
 *
 * Provides error navigation, shipment summary display, and submission controls
 * for bulk shipment upload functionality.
 */
const BulkUploadFooter: React.FC<BulkUploadFooterProps> = ({
  currentErrorCellIndex,
  errorCells,
  token,
  goToError,
  onBackToStep1,
  unauthorize,
  shipmentCount,
  totalShipments,
  totalRecords,
  onCancel,
  data,
  isRowValid,
  prepareCleanedPayload,
  apiResponse,
  showSpin,
  hideSpin,
}) => {
  return (
    /*
     * IMPORTANT: this must be a single DOM element (not a React fragment) so
     * that the resize observer in BulkUploadEditor can measure the full footer
     * height via ``:scope > :last-child``.  If it were a fragment the error-nav
     * row would be invisible to that query, the table scroll height would be
     * calculated too large, and the nav buttons would overlap the table bottom.
     */
    <div>
      <div className={styles.errorNav}>
        <Text style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          {errorCells.length === 0
            ? '0/0'
            : `${currentErrorCellIndex + 1}/${errorCells.length}`}{' '}
          errors
        </Text>

        <Button
          type='text'
          icon={
            <UpOutlined
              style={{ fontSize: 10, color: 'var(--text-primary)' }}
            />
          }
          onClick={() => goToError('up')}
          disabled={errorCells.length === 0}
        />
        <Button
          type='text'
          icon={
            <DownOutlined
              style={{ fontSize: 10, color: 'var(--text-primary)' }}
            />
          }
          onClick={() => goToError('down')}
          disabled={errorCells.length === 0}
        />
      </div>

      <div className={`bulk-upload-editor-footer ${styles.footer}`}>
        <Button
          type='default'
          onClick={onBackToStep1}
          icon={<ArrowLeftOutlined />}
          style={{
            fontWeight: 500,
            width: 220,
            height: 36,
            background: 'var(--gray-300)',
            color: 'var(--text-primary)',
            fontSize: 14,
            border: 0,
          }}
        >
          Change column matching
        </Button>

        <Space style={{ flexWrap: 'wrap', gap: 8 }}>
          <img src={shipmentCount} alt='shipment count' />
          <Text
            style={{
              color:
                totalShipments === 0
                  ? 'var(--danger)'
                  : 'var(--text-secondary)',
              fontSize: 12,
              textDecoration: 'underline dotted',
              textUnderlineOffset: 2,
            }}
          >
            <span
              className={`${styles.count} ${
                totalShipments === 0 ? styles.countEmpty : ''
              }`}
            >
              {totalShipments}
            </span>{' '}
            shipments will be created from{' '}
            <span
              className={`${styles.count} ${
                totalShipments === 0 ? styles.countEmpty : ''
              }`}
            >
              {totalRecords}
            </span>{' '}
            records
          </Text>

          <div className={`custom-button-group ${styles.buttonGroup}`}>
            <CustomButton
              key='cancel'
              width='80px'
              onClick={onCancel}
              text={'Cancel'}
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              height='36px'
            />
            <CustomButton
              key='next'
              text='Submit'
              width='80px'
              height='36px'
              borderRadius='8px'
              bgColor='var(--text-primary)'
              color='var(--surface-container)'
              border='0px'
              fontSize='14px'
              fontWeight='500'
              isFormValid={data.every((row) => !isRowValid(row))}
              onClick={() => {
                showSpin();
                const updatedApiResponse = prepareCleanedPayload(
                  apiResponse,
                  data
                );
                // Validate adds canonical multi-sensor payload under ``sensors``; it is not
                // a CSV column so it is never listed in ``header`` and must still be sent.
                // Step-2 location address columns are display-only (UUIDs live on from_location / to_location).
                const displayOnlyLocationKeys = new Set([
                  'from_location_address',
                  'to_location_address',
                ]);
                const strippedHeader = updatedApiResponse.header.filter(
                  (h) => !displayOnlyLocationKeys.has(h.key)
                );
                const allowedKeys = Array.from(
                  new Set([
                    ...strippedHeader.map((h) => h.key),
                    'status',
                    'key',
                    'sensors',
                    'from_location',
                    'to_location',
                  ])
                );
                const filteredData = updatedApiResponse.data.map((entry) => {
                  const filteredEntry: Record<string, unknown> = {};
                  allowedKeys.forEach((key) => {
                    if (Object.prototype.hasOwnProperty.call(entry, key)) {
                      filteredEntry[key] = entry[key];
                    }
                  });
                  return filteredEntry;
                });

                bulkCreate(token, {
                  ...updatedApiResponse,
                  header: strippedHeader,
                  data: filteredData,
                })
                  .then((res) => {
                    const data = res?.data?.data || null;
                    if (data?.is_success) {
                      showCustomSuccessMessage(data?.message);
                    } else {
                      showCustomSuccessMessage(data?.message);
                    }
                  })
                  .catch((error: ApiError) => {
                    unauthorize(error);
                    message.error(error?.data?.data?.message);
                  })
                  .finally(() => {
                    onCancel();
                    hideSpin();
                  });
              }}
            />
          </div>
        </Space>
      </div>
    </div>
  );
};

export default BulkUploadFooter;
